import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GridDirection,
  GridGroupSpan,
  navigateGroupedGrid,
} from "./navigateGroupedGrid";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFilter,
  selectInteractionDefinitions,
  selectMostRecentInteractions,
  selectSelectedInteractionDefinition,
  setInteractionPickerFilter,
  setSelectedInteractionDefinition,
} from "../../app/slices/InteractionPickerSlice";
import {
  BrowseDialogViewMode,
  selectBrowseDialogViewMode,
  setBrowseDialogViewMode,
} from "../../app/slices/GlobalSlice";
import useFilterableList from "../../hooks/useFilterableList";
import {
  getInteractionSearchStrings,
  InteractionCategory,
  InteractionDefintion,
  isSameInteractionDefinition,
} from "./InteractionDefinitions";

/**
 * Tile grid geometry. The column count is measured rather than fixed — the
 * dialog grows with the viewport, and `↑ ↓` step a whole row, so the count the
 * keyboard uses has to be the count on screen.
 */
// 150 tracks the Figma cards (149 wide): at Full HD the results column takes
// five ~153px columns instead of four ~194px ones, which read too wide.
export const MIN_TILE_WIDTH = 150;
export const TILE_GAP = 12;
export const DEFAULT_TILE_COLUMNS = 4;
export const MAX_TILE_COLUMNS = 6;

/** Rail order, and the order groups appear in the results. */
export const CATEGORY_ORDER: InteractionCategory[] = [
  InteractionCategory.Popups,
  InteractionCategory.Quizes,
  InteractionCategory.ThreeD,
  InteractionCategory.Other,
];

const CATEGORY_LABEL_KEYS: Record<InteractionCategory, string> = {
  [InteractionCategory.Popups]: "interaction_picker_ui.categories.popups",
  [InteractionCategory.Quizes]: "interaction_picker_ui.categories.quizzes",
  [InteractionCategory.ThreeD]: "interaction_picker_ui.categories.scene",
  [InteractionCategory.Other]: "interaction_picker_ui.categories.other",
};

/** `null` is the "All" rail entry. */
export type PickerCategoryScope = InteractionCategory | null;

export interface InteractionPickerGroup {
  key: string;
  label: string;
  /** Index of this group's first item within the flat, navigable list. */
  startIndex: number;
  items: InteractionDefintion[];
}

export interface UseInteractionPickerListOptions {
  /** When set, only these definitions are offered and the rail is suppressed. */
  definitions?: InteractionDefintion[];
  onClose: () => void;
  onAdd: (definition: InteractionDefintion) => void;
  /** Whether the dialog is on screen — the keyboard listener follows it. */
  open: boolean;
  /** Columns the tile grid is currently drawing, as measured by the results. */
  tileColumns: number;
}

/**
 * Assembles what the picker draws and what the keyboard walks: the rail scope,
 * the ordered-and-grouped result set, and the highlight.
 *
 * The flat list the arrow keys move through is built in the same order the
 * groups render, so a group's items are always contiguous and `startIndex` is
 * enough to map a rendered item back to its navigable index.
 */
export default function useInteractionPickerList(
  options: UseInteractionPickerListOptions
) {
  const { definitions, onClose, onAdd, open, tileColumns } = options;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const sliceDefinitions = useSelector(selectInteractionDefinitions);
  const allMostRecent = useSelector(selectMostRecentInteractions);
  const filter = useSelector(selectFilter);
  const viewMode = useSelector(selectBrowseDialogViewMode);
  const selectedDefinition = useSelector(selectSelectedInteractionDefinition);

  const [categoryScope, setCategoryScope] = useState<PickerCategoryScope>(null);

  const isSubset = definitions !== undefined;
  const baseDefinitions = definitions ?? sliceDefinitions;

  // A four-item subset does not need a rail, and "most recent" over a
  // restricted set is noise.
  const showRail = !isSubset;

  const railCategories = useMemo(
    () =>
      CATEGORY_ORDER.filter((category) =>
        baseDefinitions.some((definition) => definition.category === category)
      ),
    [baseDefinitions]
  );

  const scopedDefinitions = useMemo(
    () =>
      categoryScope === null
        ? baseDefinitions
        : baseDefinitions.filter(
            (definition) => definition.category === categoryScope
          ),
    [baseDefinitions, categoryScope]
  );

  const scopedRecents = useMemo(() => {
    if (isSubset) return [];

    return allMostRecent.filter((recent) =>
      scopedDefinitions.some((definition) =>
        isSameInteractionDefinition(definition, recent)
      )
    );
  }, [allMostRecent, scopedDefinitions, isSubset]);

  const isRecent = useCallback(
    (definition: InteractionDefintion) =>
      scopedRecents.some((recent) =>
        isSameInteractionDefinition(recent, definition)
      ),
    [scopedRecents]
  );

  // Recents first, then category by category — the order the groups render in,
  // so the flat index and the drawn position never disagree.
  const orderedItems = useMemo(() => {
    const rest = scopedDefinitions.filter(
      (definition) => !isRecent(definition)
    );
    const grouped = CATEGORY_ORDER.flatMap((category) =>
      rest.filter((definition) => definition.category === category)
    );
    const ungrouped = rest.filter(
      (definition) => !CATEGORY_ORDER.includes(definition.category)
    );

    return [...scopedRecents, ...grouped, ...ungrouped];
  }, [scopedDefinitions, scopedRecents, isRecent]);

  const onFilterChange = useCallback(
    (next: string) => {
      dispatch(setInteractionPickerFilter(next));
    },
    [dispatch]
  );

  // Both views group the results, so the tile grid renders as one grid per
  // group and a plain "index ± columns" step would drift whenever a group's
  // last row is ragged. The spans live in a ref because the groups are
  // computed from this hook's own output below; by the time an arrow key
  // reads them, the render that produced them has long finished.
  const groupSpansRef = useRef<GridGroupSpan[]>([]);

  const moveTileHighlight = useCallback(
    (index: number, direction: GridDirection) =>
      navigateGroupedGrid(groupSpansRef.current, tileColumns, index, direction),
    [tileColumns]
  );

  const list = useFilterableList<InteractionDefintion>({
    items: orderedItems,
    getSearchStrings: getInteractionSearchStrings,
    filter,
    onFilterChange,
    columns: viewMode === "tile" ? tileColumns : 1,
    moveHighlight: viewMode === "tile" ? moveTileHighlight : undefined,
    initiallyOpen: open,
    focusSearchOnSlash: true,
    onSelect: onAdd,
    onClose,
  });

  const { filteredItems, selectedIndex, setSelectedIndex, setOpen } = list;

  useEffect(() => {
    setOpen(open);
  }, [open, setOpen]);

  // The rail is a filter of its own, so a category switch starts at the top.
  useEffect(() => {
    setSelectedIndex(0);
  }, [categoryScope, setSelectedIndex]);

  const highlighted = filteredItems[selectedIndex];

  // The details pane and the add path both read the selection from the slice,
  // so the highlight is mirrored there rather than duplicated.
  useEffect(() => {
    if (!highlighted) return;
    if (isSameInteractionDefinition(highlighted, selectedDefinition)) return;

    dispatch(setSelectedInteractionDefinition(highlighted));
  }, [highlighted, selectedDefinition, dispatch]);

  const groups = useMemo(() => {
    const result: InteractionPickerGroup[] = [];
    let startIndex = 0;

    const push = (key: string, label: string, items: InteractionDefintion[]) => {
      if (items.length === 0) return;
      result.push({ key, label, startIndex, items });
      startIndex += items.length;
    };

    push(
      "recent",
      t("interaction_picker_ui.recently_used"),
      filteredItems.filter(isRecent)
    );

    for (const category of CATEGORY_ORDER) {
      push(
        category,
        t(CATEGORY_LABEL_KEYS[category]),
        filteredItems.filter(
          (definition) =>
            !isRecent(definition) && definition.category === category
        )
      );
    }

    return result;
  }, [filteredItems, isRecent, t]);

  groupSpansRef.current = groups.map((group) => ({
    startIndex: group.startIndex,
    count: group.items.length,
  }));

  const changeViewMode = useCallback(
    (mode: BrowseDialogViewMode) => {
      dispatch(setBrowseDialogViewMode(mode));
    },
    [dispatch]
  );

  const getCategoryLabel = useCallback(
    (category: InteractionCategory) => t(CATEGORY_LABEL_KEYS[category]),
    [t]
  );

  return {
    filter,
    setFilter: list.setFilter,
    searchInputRef: list.searchInputRef,
    groups,
    flatItems: filteredItems,
    selectedIndex,
    setSelectedIndex,
    highlighted,
    viewMode,
    changeViewMode,
    showRail,
    railCategories,
    categoryScope,
    setCategoryScope,
    getCategoryLabel,
  };
}

export type InteractionPickerList = ReturnType<typeof useInteractionPickerList>;
